import { useEffect } from 'react'
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom'

import { DashboardLayout } from '@/app/layouts/DashboardLayout'
import { ProtectedRoute } from '@/app/router/ProtectedRoute'
import { PublicOnlyRoute } from '@/app/router/PublicOnlyRoute'
import LoginPage from '@/features/auth/pages/LoginPage'
import WelcomeLoaderPage from '@/features/auth/pages/WelcomeLoaderPage'
import { useAuthStore } from '@/features/auth/stores/auth.store'
import AdminCustomersPage from '@/modules/admin/pages/AdminCustomersPage'
import AdminDashboardPage from '@/modules/admin/pages/AdminDashboardPage'
import AdminDCRPage from '@/modules/admin/pages/AdminDCRPage'
import AdminLeadsRoute from '@/modules/admin/pages/AdminLeadsRoute'
import AdminMarketingRoute from '@/modules/admin/pages/AdminMarketingRoute'
import AdminPurchaseRoute from '@/modules/admin/pages/AdminPurchaseRoute'
import AdminQuotationPdfPreviewPage from '@/modules/admin/pages/AdminQuotationPdfPreviewPage'
import AdminQuotationsRoute from '@/modules/admin/pages/AdminQuotationsRoute'
import AdminSalesRoute from '@/modules/admin/pages/AdminSalesRoute'
import MarketingDashboardPage from '@/modules/marketing/pages/MarketingDashboardPage'
import MarketingLeadsRoute from '@/modules/marketing/pages/MarketingLeadsRoute'
import PurchaseOrderPdfPreviewPage from '@/modules/purchase/pages/PurchaseOrderPdfPreviewPage'
import PurchaseDashboardPage from '@/modules/purchase/pages/PurchasePage'
import QuotationPdfPreviewPage from '@/modules/sales/pages/QuotationPdfPreviewPage'
import SalesCreateQuotationPage from '@/modules/sales/pages/SalesCreateQuotationPage'
import SalesCustomersPage from '@/modules/sales/pages/SalesCustomersPage'
import SalesDashboardPage from '@/modules/sales/pages/SalesDashboardPage'
import SalesDCRPage from '@/modules/sales/pages/SalesDCRPage'
import SalesLeadsPage from '@/modules/sales/pages/SalesLeadsPage'
import SalesQuotationsPage from '@/modules/sales/pages/SalesQuotationsPage'
import { ROLES } from '@/shared/constants/roles'
import { ROUTES } from '@/shared/constants/routes'

function AuthBootstrap({ children }) {
  const bootstrap = useAuthStore((state) => state.bootstrap)

  useEffect(() => {
    bootstrap()
  }, [bootstrap])

  return children
}

export function AppRouter() {
  return (
    <BrowserRouter>
      <AuthBootstrap>
        <Routes>
          {/* Public Routes */}
          <Route element={<PublicOnlyRoute />}>
            <Route path={ROUTES.LOGIN} element={<LoginPage />} />
          </Route>

          {/* Welcome Loader */}
          <Route element={<ProtectedRoute />}>
            <Route path={ROUTES.WELCOME} element={<WelcomeLoaderPage />} />
          </Route>

          {/* ================= ADMIN ================= */}
          <Route element={<ProtectedRoute allowedRoles={[ROLES.ADMIN]} />}>
            <Route path={ROUTES.ADMIN} element={<DashboardLayout />}>
              <Route index element={<AdminDashboardPage />} />

              <Route path="leads" element={<AdminLeadsRoute />} />

              <Route path="quotes" element={<AdminQuotationsRoute />} />

              <Route
                path="quotes/preview"
                element={<AdminQuotationPdfPreviewPage />}
              />

              <Route path="sales" element={<AdminSalesRoute />} />

              <Route path="customers" element={<AdminCustomersPage />} />

              <Route path="marketing" element={<AdminMarketingRoute />} />

              <Route path="purchase" element={<AdminPurchaseRoute />} />

              {/* Create quotation from a lead */}
              <Route
                path="leads/create-quotation/:leadId"
                element={<SalesCreateQuotationPage />}
              />

              {/* Edit quotation - reuses sales create page */}
              <Route
                path="quotes/edit/:quotationId"
                element={<SalesCreateQuotationPage />}
              />

              <Route path="dcr" element={<AdminDCRPage />} />
            </Route>
          </Route>

          {/* ================= SALES ================= */}
          <Route element={<ProtectedRoute allowedRoles={[ROLES.SALES]} />}>
            <Route path={ROUTES.SALES} element={<DashboardLayout />}>
              <Route index element={<SalesDashboardPage />} />

              <Route path="leads" element={<SalesLeadsPage />} />

              <Route path="quotes" element={<SalesQuotationsPage />} />

              <Route path="customers" element={<SalesCustomersPage />} />

              <Route
                path="quotes/preview"
                element={<QuotationPdfPreviewPage />}
              />

              <Route
                path="leads/create-quotation/:leadId"
                element={<SalesCreateQuotationPage />}
              />

              <Route path="dcr" element={<SalesDCRPage />} />

              {/* Edit quotation - reuses the same create page in edit mode */}
              <Route
                path="quotes/edit/:quotationId"
                element={<SalesCreateQuotationPage />}
              />
            </Route>
          </Route>

          {/* ================= MARKETING ================= */}
          <Route element={<ProtectedRoute allowedRoles={[ROLES.MARKETING]} />}>
            <Route path={ROUTES.MARKETING} element={<DashboardLayout />}>
              <Route index element={<MarketingDashboardPage />} />

              <Route path="leads" element={<MarketingLeadsRoute />} />
            </Route>
          </Route>

          {/* ================= PURCHASE ================= */}
          <Route element={<ProtectedRoute allowedRoles={[ROLES.PURCHASE]} />}>
            <Route path={ROUTES.PURCHASE} element={<DashboardLayout />}>
              <Route index element={<PurchaseDashboardPage />} />
              <Route path="preview" element={<PurchaseOrderPdfPreviewPage />} />
            </Route>
          </Route>

          {/* Default Routes */}
          <Route
            path={ROUTES.ROOT}
            element={<Navigate to={ROUTES.LOGIN} replace />}
          />

          <Route path="*" element={<Navigate to={ROUTES.LOGIN} replace />} />
        </Routes>
      </AuthBootstrap>
    </BrowserRouter>
  )
}
